import { Link } from "react-router-dom";
import { Repeat, HeartPulse, Users, TrendingUp } from "lucide-react";
import { IMAGES } from "../data/site";
import { Reveal, Label, BookButton, Marquee } from "../components/ui-kit";

const STATIONS = [
  { d: "1,000m SkiErg", note: "Full-body pull — arms, shoulders and core." },
  { d: "50m Sled Push", note: "Drive a weighted sled. Legs and lungs." },
  { d: "50m Sled Pull", note: "Haul it back — glutes, back and grip." },
  { d: "80m Burpee Broad Jumps", note: "Chest to floor, jump forward, repeat." },
  { d: "1,000m Row", note: "The halfway grind. Full-body power." },
  { d: "200m Farmers Carry", note: "Two kettlebells, one steady walk." },
  { d: "100m Sandbag Lunges", note: "A sandbag on your back, knee to the floor." },
  { d: "100 Wall Balls", note: "Squat, throw to target, catch, go again." },
];

const METHOD = [
  { icon: HeartPulse, title: "Run + Strength, together", desc: "Hyrox blends running with functional strength. We train both every week so neither one becomes your weakness." },
  { icon: Repeat, title: "Practice the movements", desc: "Sleds, carries, wall balls, rowing — we coach the exact stations so race day feels familiar, not scary." },
  { icon: TrendingUp, title: "Build your engine", desc: "Smart intervals and conditioning raise your stamina gradually, so you can keep moving when it counts." },
  { icon: Users, title: "Scaled to you", desc: "Every workout is scaled to your level. You're training for Hyrox whether you ever race or not." },
];

export default function WhatIsHyrox() {
  return (
    <>
      <section className="relative pt-40 pb-24 grain overflow-hidden" data-testid="hyrox-hero">
        <img src={IMAGES.skierg} alt="Hyrox-style training" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Label>The Fitness Race</Label>
          <h1 className="font-display uppercase text-white text-5xl md:text-8xl leading-[0.9] mt-6 max-w-4xl">What is Hyrox?</h1>
          <p className="mt-8 text-lg md:text-xl text-lightgrey max-w-2xl">Hyrox is a global fitness race designed for everyday people — not just elite athletes. Same workout, same standards, anywhere in the world. And it's the perfect goal to train toward, even if you never step on the start line.</p>
          <div className="mt-10"><BookButton>Start Training for It</BookButton></div>
        </div>
      </section>

      <section className="bg-black py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Label className="justify-center">In Plain Terms</Label>
            <p className="mt-8 font-display text-3xl md:text-5xl text-white leading-tight uppercase">
              You run <span className="text-gold">8 kilometres</span> — and between every single kilometre, you tackle <span className="text-gold">one workout station</span>.
            </p>
            <p className="mt-6 text-lightgrey text-lg max-w-2xl mx-auto">That's it. Eight runs, eight functional workouts, done back to back. No complicated skills, no surprises — just honest, full-body fitness that anyone can build toward with the right coaching.</p>
          </Reveal>
        </div>
      </section>

      <Marquee items={["Run", "SkiErg", "Sled Push", "Row", "Carry", "Lunge", "Wall Balls"]} />

      <section className="bg-black py-24 md:py-32" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Label>How It Works</Label></Reveal>
          <Reveal delay={0.1}><h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none max-w-2xl">1 run. 1 station.<br />Eight times over.</h2></Reveal>
          <p className="mt-5 text-lightgrey max-w-2xl">Every athlete does the exact same race. You run 1km, complete a station, then head back out for the next kilometre. The clock never stops — but you move at your pace.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-14">
            {STATIONS.map((s, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06}>
                <div className="flex items-center gap-5 rounded-2xl border border-white/10 bg-charcoal p-6 hover:border-gold/40 transition" data-testid={`station-${i}`}>
                  <span className="font-display text-4xl text-gold shrink-0 w-14">{String(i + 1).padStart(2, "0")}</span>
                  <div className="border-l border-white/10 pl-5">
                    <p className="font-bold text-white text-lg">{s.d}</p>
                    <p className="text-sm text-lightgrey/80 mt-1">{s.note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-sm text-white/40">Weights and reps scale by division (Open, Pro, Doubles & Relay) — so there's a start line for everyone, from first-timers to seasoned racers.</p>
        </div>
      </section>

      <section className="bg-charcoal py-24 md:py-32 grain relative" data-testid="methodology">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <Reveal><Label>How We Train You</Label></Reveal>
          <Reveal delay={0.1}><h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none max-w-2xl">Our methodology.</h2></Reveal>
          <p className="mt-5 text-lightgrey max-w-2xl">You don't need to be fit to start — you get fit by starting. Our coached classes are built around the demands of Hyrox, blending strength and endurance so your whole body gets stronger and more capable.</p>
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {METHOD.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.07}>
                <div className="rounded-3xl border border-white/10 bg-black/40 p-8 h-full hover:border-gold/40 hover:-translate-y-1 transition-all duration-300">
                  <span className="inline-flex w-12 h-12 rounded-xl bg-gold/15 border border-gold/30 items-center justify-center text-gold mb-6"><m.icon size={22} /></span>
                  <h3 className="font-display text-2xl text-white uppercase">{m.title}</h3>
                  <p className="mt-3 text-lightgrey">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-28 overflow-hidden" data-testid="hyrox-cta">
        <img src={IMAGES.hyroxAthlete} alt="HFC member training" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display uppercase text-white text-4xl md:text-6xl leading-none">Think you can't<br />do Hyrox?</h2>
            <p className="mt-6 text-lg text-lightgrey max-w-xl mx-auto">You can — and we'll get you there. Start with a free intro and we'll show you exactly how.</p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <BookButton>Book Your Free Intro</BookButton>
              <Link to="/programs" className="inline-flex items-center rounded-full border border-gold text-gold font-bold px-8 py-4 hover:bg-gold hover:text-black transition" data-testid="hyrox-programs">See Our Programs</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
